/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import TimePicker from "../../../common/TimePicker";
import {
    getActivityItinerary,
    createActivityItinerary,
    deleteActivityItinerary,
    updateActivityItinerary,
    getActivityTickets,
} from "../../../../services/activityService";
import {
    abAddBtn,
    abDeleteBtn,
    abEmptyState,
    abItemCardShell,
    abSectionTitle,
} from "../activityBuilderUi";

const formatTimeForDisplay = (time) => {
    if (!time) return "";

    const normalized = normalizeTimeTo24Hour(time);
    const match = String(normalized).match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return time;

    const hour24 = parseInt(match[1], 10);
    const minutes = match[2];
    let hour12 = hour24;
    let period = "AM";

    if (hour24 === 0) {
        hour12 = 12;
    } else if (hour24 === 12) {
        hour12 = 12;
        period = "PM";
    } else if (hour24 > 12) {
        hour12 = hour24 - 12;
        period = "PM";
    }

    return `${hour12}:${minutes} ${period}`;
};

const normalizeTimeTo24Hour = (time) => {
    if (!time) return "";
    if (/am|pm/i.test(time)) {
        const cleaned = String(time).replace(/\s+/g, "").toLowerCase();
        const match = cleaned.match(/^(\d{1,2}):?(\d{2})?(am|pm)$/);
        if (!match) return time;

        let hour = parseInt(match[1], 10);
        const minute = match[2] ? match[2].padStart(2, "0") : "00";
        const period = match[3];

        if (period === "am" && hour === 12) hour = 0;
        else if (period === "pm" && hour !== 12) hour += 12;

        return `${hour.toString().padStart(2, "0")}:${minute}`;
    }

    const match = String(time).match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
        return `${match[1].padStart(2, "0")}:${match[2]}`;
    }

    return time;
};

const DeleteConfirmationModal = ({ isOpen, onCancel, onConfirm, title, message }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="p-6">
                    <div className="flex items-center space-x-3 text-red-600 mb-4">
                        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                            <i className="fi fi-rr-triangle-warning text-xl"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-500 text-sm font-medium leading-relaxed">
                        {message}
                    </p>
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                    <button
                        onClick={onCancel}
                        className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-white transition-all active:scale-95"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-8 py-2.5 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 active:scale-95"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

const packageFieldClass =
    "placeholder:text-sm text-sm font-medium text-gray-800 placeholder:font-normal w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-none";

const ItinerarySection = ({
    activityId,
    ticketId,
    title,
    isExpanded,
    onToggle,
    embedded = false,
}) => {
    const [titleInput, setTitleInput] = useState("");
    const [timeInput, setTimeInput] = useState("");
    const [descInput, setDescInput] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });
    
    const queryClient = useQueryClient();

    // Fetch itinerary specifically for this ticket
    const { data: itineraryData, isLoading, refetch } = useQuery({
        queryKey: ["activityItinerary", activityId, ticketId],
        queryFn: () => getActivityItinerary(activityId, ticketId),
        enabled: !!activityId && !!ticketId,
    });

    const items = itineraryData?.data || [];

    const handleAdd = async () => {
        if (!titleInput.trim() || !descInput.trim()) return;
        setIsSaving(true);
        try {
            // The store API expects an array of itinerary steps
            const newStep = {
                step_number: items.length + 1,
                title: titleInput.trim(),
                time: timeInput ? formatTimeForDisplay(timeInput) : "",
                description: descInput.trim(),
            };
            
            const newList = [...items.map(item => ({
                step_number: item.step_number,
                title: item.title,
                time: item.time,
                description: item.description
            })), newStep];

            await createActivityItinerary(activityId, ticketId, { itinerary: newList });
            setTitleInput("");
            setTimeInput("");
            setDescInput("");
            await refetch();
        } catch (error) {
            console.error("Failed to add itinerary step", error);
        } finally {
            setIsSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteModal.id) return;
        try {
            await deleteActivityItinerary(activityId, ticketId, deleteModal.id);
            setDeleteModal({ isOpen: false, id: null });
            await refetch();
        } catch (error) {
            console.error("Failed to delete itinerary step", error);
        }
    };

    const showContent = embedded || isExpanded;

    const addStopForm = (
        <div className="space-y-4">
            <p className="text-sm font-bold text-gray-900">Add new stop</p>
            <div className="flex items-end gap-4">
                <div className="min-w-0 w-full max-w-xs">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                        Title <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={titleInput}
                        onChange={(e) => setTitleInput(e.target.value)}
                        placeholder="e.g. Arrival at Mosque"
                        className={packageFieldClass}
                    />
                </div>
                <div className="w-52 shrink-0">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                        Estimated Time (Optional)
                    </label>
                    <TimePicker
                        value={timeInput}
                        onChange={setTimeInput}
                        placeholder="Select pickup time"
                    />
                </div>
            </div>

            <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                    Description <span className="text-red-500">*</span>
                </label>
                <textarea
                    value={descInput}
                    onChange={(e) => setDescInput(e.target.value)}
                    placeholder="Describe what happens at this stop..."
                    rows={3}
                    className={`${packageFieldClass} min-h-[88px] resize-y py-2`}
                />
            </div>

            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={handleAdd}
                    disabled={isSaving || !titleInput.trim() || !descInput.trim()}
                    className={`${abAddBtn} disabled:cursor-not-allowed disabled:opacity-50`}
                >
                    {isSaving ? "Saving..." : (
                        <>
                            <i className="fi fi-rr-plus text-xs"></i>
                            Add to itinerary
                        </>
                    )}
                </button>
            </div>
        </div>
    );

    const timelineSection = (
        <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Timeline</label>
            <div className="relative ml-3 space-y-5 pl-7 before:absolute before:bottom-2 before:left-0 before:top-2 before:w-[2px] before:bg-gradient-to-b before:from-primary/40 before:via-primary/20 before:to-transparent before:content-['']">
                {isLoading ? (
                    <div className="flex items-center justify-center space-x-2 py-4 text-gray-400">
                        <i className="fi fi-rr-spinner animate-spin"></i>
                        <span className="text-xs font-medium">Fetching details...</span>
                    </div>
                ) : items.length === 0 ? (
                    <div className="-ml-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/50 py-8 text-gray-400">
                        <i className="fi fi-rr-map-marker mb-1 text-2xl opacity-20"></i>
                        <p className="text-xs italic">No stops added to the timeline yet</p>
                    </div>
                ) : (
                    items.map((item, index) => (
                        <div
                            key={item.id}
                            className="group relative animate-in slide-in-from-left-2 duration-300"
                            style={{ animationDelay: `${index * 50}ms` }}
                        >
                            <div className="absolute -left-[36px] top-1.5 z-10 h-4 w-4 rounded-full border-4 border-primary bg-white shadow-sm"></div>

                            <div className="flex items-start justify-between rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm transition-all hover:border-gray-200 hover:bg-gray-50 hover:shadow-md">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-3">
                                        {item.time && (
                                            <span className="rounded-md bg-gray-900 px-2 py-0.5 text-[10px] font-semibold tracking-wider text-white">
                                                {formatTimeForDisplay(item.time)}
                                            </span>
                                        )}
                                        <h5 className="text-sm font-semibold tracking-tight text-gray-900">{item.title}</h5>
                                    </div>
                                    <p className="max-w-2xl text-xs font-medium leading-relaxed text-gray-500">
                                        {item.description}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setDeleteModal({ isOpen: true, id: item.id })}
                                    className={abDeleteBtn}
                                >
                                    <i className="fi fi-rr-trash text-xs"></i>
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );

    const sectionBody = showContent ? (
        <div className={`space-y-6 ${embedded ? "" : "p-4"}`}>
            {addStopForm}
            {timelineSection}
        </div>
    ) : null;

    if (embedded) {
        return (
            <>
                {sectionBody}
                <DeleteConfirmationModal
                    isOpen={deleteModal.isOpen}
                    onCancel={() => setDeleteModal({ isOpen: false, id: null })}
                    onConfirm={confirmDelete}
                    title="Delete Stop"
                    message="Are you sure you want to remove this stop from the itinerary? This will reorder the remaining steps."
                />
            </>
        );
    }

    return (
        <>
            <div className={`${abItemCardShell} overflow-hidden transition-all duration-300`}>
                <button
                    type="button"
                    onClick={onToggle}
                    className="flex w-full items-center justify-between border-b border-gray-100 bg-gray-50/50 px-4 py-3 transition-colors hover:bg-gray-50"
                >
                    <div className="flex items-center space-x-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${isExpanded ? 'bg-primary/20 text-primary' : 'bg-gray-100 text-gray-400'}`}>
                            <i className="fi fi-rr-calendar-clock"></i>
                        </div>
                        <div className="text-left">
                            <h4 className="text-sm font-semibold text-gray-900 tracking-tight">{title}</h4>
                            <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wider">
                                {isLoading ? 'Loading...' : `${items.length} Stops`}
                            </p>
                        </div>
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-white border border-gray-100 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                        <i className="fi fi-rr-angle-small-down text-gray-400"></i>
                    </div>
                </button>

                {sectionBody}
            </div>

            <DeleteConfirmationModal 
                isOpen={deleteModal.isOpen}
                onCancel={() => setDeleteModal({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Delete Stop"
                message="Are you sure you want to remove this stop from the itinerary? This will reorder the remaining steps."
            />
        </>
    );
};

const ActivityItinerary = ({ activityId, singleTicketId, singleTicketName }) => {
    // Fetch Tickets
    const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
        queryKey: ["activityTickets", activityId],
        queryFn: () => getActivityTickets(activityId),
        enabled: !!activityId && !singleTicketId,
    });
    const tickets = singleTicketId
        ? [{ id: singleTicketId, name: singleTicketName || "Ticket" }]
        : ticketsData?.data || [];
    const [expandedTicketId, setExpandedTicketId] = useState(null);

    if (!activityId) {
        return <div className="text-gray-500">Please save General Info first.</div>;
    }

    if (singleTicketId) {
        return (
            <ItinerarySection
                activityId={activityId}
                ticketId={singleTicketId}
                title={singleTicketName || "Ticket"}
                isExpanded
                embedded
                onToggle={() => {}}
            />
        );
    }

    if (!singleTicketId && ticketsLoading) return (
        <div className="flex flex-col items-center justify-center p-20 text-gray-400">
            <i className="fi fi-rr-spinner animate-spin text-4xl mb-4"></i>
            <span className="font-bold uppercase tracking-widest text-[11px]">Building itinerary...</span>
        </div>
    );

    return (
        <div className="space-y-4">
            <div className={`${abEmptyState} space-y-4`}>
                <h4 className={abSectionTitle}>Ticket itinerary</h4>
                {tickets.length > 0 ? (
                    tickets.map((ticket, index) => {
                        const expanded =
                            expandedTicketId === null ? index === 0 : expandedTicketId === ticket.id;
                        return (
                        <ItinerarySection
                            key={ticket.id}
                            activityId={activityId}
                            ticketId={ticket.id}
                            title={ticket.name}
                            isExpanded={expanded}
                            onToggle={() =>
                                setExpandedTicketId((prev) =>
                                    prev === ticket.id ? null : ticket.id
                                )
                            }
                        />
                        );
                    })
                ) : (
                    <div className="rounded-lg border border-dashed border-gray-200 bg-white py-8 text-center text-sm text-gray-500">
                        <i className="fi fi-rr-ticket-alt mb-2 inline-block text-2xl text-gray-300"></i>
                        <p className="font-medium text-gray-700">No tickets found</p>
                        <p className="mt-1 text-xs text-gray-500">Add tickets in the Tickets tab to start mapping itineraries.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActivityItinerary;
